import { useState, useEffect } from 'react'
import { Calendar, FileText, AlertCircle, CheckCircle, Clock, DollarSign, TrendingUp, RefreshCw, AlertTriangle, Info, Settings, Building2, Save, History, ChevronDown, ChevronUp, X, Bell, Calculator, ChevronLeft, ChevronRight } from 'lucide-react'
import { partnerAdminApi, clientTaxSettingsApi } from '../services/api'
import type { TaxCalendarItem, TaxOverview, ComplianceReport, TaxCalendarResponse, ClientTaxSettings, TaxRegimeInfo, AccessoryObligation, ISSRate, SimplesNacionalAnnex, SimplesNacionalCalculation, TaxSettingsHistoryEntry, TaxRegime } from '../services/api'
import { Select } from '../components/ui/Select'

// Helper para gerar opções de meses
const generateMonthOptions = () => {
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ]
  const options: { value: string; label: string }[] = []
  const now = new Date()

  // Gera 12 meses para trás e 6 para frente
  for (let i = -12; i <= 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1)
    const value = date.toISOString().slice(0, 7) // "2026-01"
    const label = `${months[date.getMonth()]} ${date.getFullYear()}`
    options.push({ value, label })
  }

  return options
}

const monthOptions = generateMonthOptions()

/**
 * Tax Management Page for Partner Admin
 *
 * Purpose: Manage tax obligations and calculations for partner's clients
 * Features:
 * - View tax calendar for all clients
 * - Calculate taxes (DAS, IRPF, ISS)
 * - Track payment status
 * - Generate tax reports
 * - Monitor compliance
 *
 * Data Isolation: Only shows tax data for clients belonging to current partner
 */

// Pós-MVP: Configurações por Cliente desabilitado
// Não há cálculo automático de impostos implementado. Ver docs/POS-MVP.md seção 8.3
const SHOW_SETTINGS_TAB = false

export function TaxManagement() {
  const [calendarData, setCalendarData] = useState<TaxCalendarResponse | null>(null)
  const [overview, setOverview] = useState<TaxOverview | null>(null)
  const [complianceReport, setComplianceReport] = useState<ComplianceReport | null>(null)
  const [loadingReport, setLoadingReport] = useState(false)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'calendar' | 'reports' | 'settings'>('calendar')
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7))
  const [error, setError] = useState<string | null>(null)
  const [showNotifications, setShowNotifications] = useState(false)

  // Pagination for calendar
  const [calendarPage, setCalendarPage] = useState(1)
  const calendarItemsPerPage = 10

  // Client Tax Settings state
  const [clientsList, setClientsList] = useState<{ id: string; name: string }[]>([])
  const [settingsSelectedClientId, setSettingsSelectedClientId] = useState<string>('')
  const [clientTaxSettings, setClientTaxSettings] = useState<ClientTaxSettings | null>(null)
  const [taxRegimes, setTaxRegimes] = useState<TaxRegimeInfo[]>([])
  const [accessoryObligations, setAccessoryObligations] = useState<AccessoryObligation[]>([])
  const [issRates, setIssRates] = useState<ISSRate[]>([])
  const [simplesAnnexes, setSimplesAnnexes] = useState<SimplesNacionalAnnex[]>([])
  const [settingsHistory, setSettingsHistory] = useState<TaxSettingsHistoryEntry[]>([])
  const [loadingSettings, setLoadingSettings] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    regime: true,
    simplesNacional: false,
    iss: false,
    withholdings: false,
    obligations: false,
    notifications: false
  })
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [simplesCalculation, setSimplesCalculation] = useState<SimplesNacionalCalculation | null>(null)

  // Load data from API
  useEffect(() => {
    loadTaxData()
    setCalendarPage(1) // Reset pagination when month changes
  }, [selectedMonth])

  const loadTaxData = async () => {
    setLoading(true)
    setError(null)

    try {
      const [year, month] = selectedMonth.split('-')

      // Load overview and calendar in parallel - both use selected month
      const [overviewData, calendarResponse] = await Promise.all([
        partnerAdminApi.tax.getOverview({
          month: parseInt(month),
          year: parseInt(year)
        }),
        partnerAdminApi.tax.getCalendar({
          month: parseInt(month),
          year: parseInt(year)
        })
      ])

      setOverview(overviewData)
      setCalendarData(calendarResponse)
    } catch (err: any) {
      console.error('Error loading tax data:', err)
      setError(err.response?.data?.message || 'Erro ao carregar dados fiscais')
    } finally {
      setLoading(false)
    }
  }

  const loadComplianceReport = async () => {
    setLoadingReport(true)
    setComplianceReport(null) // Limpa relatório anterior para mostrar loading
    try {
      // Últimos 30 dias como padrão
      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 30)

      // Delay mínimo de 800ms para feedback visual
      const [report] = await Promise.all([
        partnerAdminApi.tax.getComplianceReport({
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }),
        new Promise(resolve => setTimeout(resolve, 800))
      ])

      setComplianceReport(report)
    } catch (err: any) {
      console.error('Error loading compliance report:', err)
      setError(err.response?.data?.message || 'Erro ao carregar relatório de compliance')
    } finally {
      setLoadingReport(false)
    }
  }

  // Load reference data for tax settings
  const loadTaxSettingsReferenceData = async () => {
    try {
      const [regimes, obligations, annexes, clientsResponse] = await Promise.all([
        clientTaxSettingsApi.getTaxRegimes(),
        clientTaxSettingsApi.getAccessoryObligations(),
        clientTaxSettingsApi.getSimplesNacionalAnnexes(),
        partnerAdminApi.clients.list()
      ])
      setTaxRegimes(regimes)
      setAccessoryObligations(obligations)
      setSimplesAnnexes(annexes)
      setClientsList(clientsResponse.clients.map((c: any) => ({ id: c.id, name: c.name })))
    } catch (err: any) {
      console.error('Error loading tax settings reference data:', err)
    }
  }

  // Load client tax settings
  const loadClientTaxSettings = async (clientId: string) => {
    setLoadingSettings(true)
    setSettingsError(null)
    setSettingsSuccess(null)
    setSimplesCalculation(null)

    try {
      const [settings, history] = await Promise.all([
        clientTaxSettingsApi.getSettings(clientId),
        clientTaxSettingsApi.getSettingsHistory(clientId, 10)
      ])
      setClientTaxSettings(settings)
      setSettingsHistory(history)
      setSettingsSelectedClientId(clientId)

      // Load ISS rates for client's state if available
      if (settings.iss?.municipalityCode) {
        const uf = settings.iss.municipalityCode.substring(0, 2)
        const rates = await clientTaxSettingsApi.getISSRates(uf)
        setIssRates(rates)
      }
    } catch (err: any) {
      console.error('Error loading client tax settings:', err)
      setSettingsError(err.response?.data?.message || 'Erro ao carregar configurações fiscais')
    } finally {
      setLoadingSettings(false)
    }
  }

  // Save client tax settings
  const saveClientTaxSettings = async () => {
    if (!settingsSelectedClientId || !clientTaxSettings) return

    setSavingSettings(true)
    setSettingsError(null)
    setSettingsSuccess(null)

    try {
      const updated = await clientTaxSettingsApi.updateSettings(settingsSelectedClientId, clientTaxSettings)
      setClientTaxSettings(updated)
      setSettingsSuccess('Configurações salvas com sucesso!')

      // Reload history
      const history = await clientTaxSettingsApi.getSettingsHistory(settingsSelectedClientId, 10)
      setSettingsHistory(history)
    } catch (err: any) {
      console.error('Error saving client tax settings:', err)
      setSettingsError(err.response?.data?.message || 'Erro ao salvar configurações fiscais')
    } finally {
      setSavingSettings(false)
    }
  }

  // Calculate Simples Nacional rate
  const calculateSimplesRate = async () => {
    if (!settingsSelectedClientId || !clientTaxSettings?.simplesNacional) return

    try {
      const result = await clientTaxSettingsApi.calculateSimplesNacionalRate(
        settingsSelectedClientId,
        {
          rbt12: clientTaxSettings.simplesNacional.rbt12 || 0,
          annex: clientTaxSettings.simplesNacional.annex,
          factorR: clientTaxSettings.simplesNacional.factorR
        }
      )
      setSimplesCalculation(result)
    } catch (err: any) {
      console.error('Error calculating Simples rate:', err)
      setSettingsError(err.response?.data?.message || 'Erro ao calcular alíquota do Simples')
    }
  }

  // Load ISS rates by UF
  const loadISSRates = async (uf: string) => {
    try {
      const rates = await clientTaxSettingsApi.getISSRates(uf)
      setIssRates(rates)
    } catch (err: any) {
      console.error('Error loading ISS rates:', err)
    }
  }

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
  }

  // Update nested settings
  const updateSettings = (path: string, value: any) => {
    if (!clientTaxSettings) return

    const keys = path.split('.')
    const newSettings = { ...clientTaxSettings }
    let current: any = newSettings

    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {}
      }
      current[keys[i]] = { ...current[keys[i]] }
      current = current[keys[i]]
    }

    current[keys[keys.length - 1]] = value
    setClientTaxSettings(newSettings)
  }

  // Toggle accessory obligation
  const toggleObligation = (code: string) => {
    if (!clientTaxSettings) return

    const current = clientTaxSettings.accessoryObligations || []
    const newObligations = current.includes(code)
      ? current.filter(c => c !== code)
      : [...current, code]

    setClientTaxSettings({ ...clientTaxSettings, accessoryObligations: newObligations })
  }

  const handleMarkAsPaid = async (obligation: TaxCalendarItem) => {
    try {
      await partnerAdminApi.tax.processPayment(
        obligation.clientId,
        obligation.id,
        {
          amount: obligation.amount,
          paidAt: new Date().toISOString(),
          paymentMethod: 'manual'
        }
      )
      // Reload data
      loadTaxData()
    } catch (err: any) {
      console.error('Error marking as paid:', err)
      setError(err.response?.data?.message || 'Erro ao marcar como pago')
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'overdue': return 'bg-red-100 text-red-800'
      case 'calculated': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />
      case 'pending': return <Clock className="w-4 h-4" />
      case 'overdue': return <AlertCircle className="w-4 h-4" />
      case 'calculated': return <Calculator className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid': return 'Pago'
      case 'pending': return 'Pendente'
      case 'overdue': return 'Vencido'
      case 'calculated': return 'Calculado'
      default: return status
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'border-l-red-500'
      case 'medium': return 'border-l-yellow-500'
      case 'low': return 'border-l-green-500'
      default: return 'border-l-gray-500'
    }
  }

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical': return <AlertCircle className="w-5 h-5 text-red-600" />
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-600" />
      case 'info': return <Info className="w-5 h-5 text-blue-600" />
      default: return <Info className="w-5 h-5 text-gray-600" />
    }
  }

  const getAlertBg = (type: string) => {
    switch (type) {
      case 'critical': return 'bg-red-50 border-red-200'
      case 'warning': return 'bg-yellow-50 border-yellow-200'
      case 'info': return 'bg-blue-50 border-blue-200'
      default: return 'bg-gray-50 border-gray-200'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão Fiscal</h1>
          <p className="text-gray-600 mt-1">
            Gerencie obrigações fiscais e cálculos de impostos dos seus clientes
          </p>
        </div>
        <div className="flex gap-2">
          {/* Notifications Button */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors relative"
            >
              <Bell className="w-4 h-4" />
              <span>Alertas</span>
              {overview?.alerts && overview.alerts.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {overview.alerts.length}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowNotifications(false)} />
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <h3 className="font-semibold text-gray-900">Notificações</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {overview?.alerts && overview.alerts.length > 0 ? (
                      overview.alerts.map((alert, index) => (
                        <div key={index} className={`px-4 py-3 border-b border-gray-100 last:border-b-0 ${alert.type === 'critical' ? 'bg-red-50' : alert.type === 'warning' ? 'bg-yellow-50' : 'bg-white'}`}>
                          <div className="flex items-start gap-3">
                            {getAlertIcon(alert.type)}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 text-sm">{alert.title}</p>
                              <p className="text-xs text-gray-600 mt-0.5">{alert.description}</p>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        Nenhuma notificação
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            onClick={loadTaxData}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Obrigações</p>
              <p className="text-2xl font-bold text-gray-900">{overview?.overview?.totalObligations || 0}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pendentes</p>
              <p className="text-2xl font-bold text-orange-600">{overview?.breakdown?.byStatus?.pending || 0}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-purple-600">
                R$ {(overview?.overview?.totalAmount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pagos</p>
              <p className="text-2xl font-bold text-green-600">{overview?.overview?.paidObligations || 0}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('calendar')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'calendar'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Calendário Fiscal
              </div>
            </button>

            {/* TODO: Pós-MVP - Relatórios fiscais precisam ser melhor elaborados
            <button
              onClick={() => {
                setActiveTab('reports')
                loadComplianceReport()
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Relatórios
              </div>
            </button>
            */}
            {SHOW_SETTINGS_TAB && (
              <button
                onClick={() => {
                  setActiveTab('settings')
                  loadTaxSettingsReferenceData()
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'settings'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Configurações por Cliente
                </div>
              </button>
            )}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'calendar' && (
            <div className="space-y-4">
              {/* Month Selector */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Calendário de Obrigações Fiscais</h3>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  options={monthOptions}
                />
              </div>



              {/* Upcoming Deadlines */}
              {calendarData?.upcomingDeadlines && calendarData.upcomingDeadlines.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-md font-medium text-gray-900 mb-2">Próximos Vencimentos</h4>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {calendarData.upcomingDeadlines.map((item) => (
                      <div
                        key={item.id}
                        className={`flex-shrink-0 p-3 bg-white border-l-4 rounded-lg shadow-sm ${getPriorityColor(item.priority)}`}
                      >
                        <p className="font-medium text-gray-900">{item.taxType}</p>
                        <p className="text-sm text-gray-600">{new Date(item.dueDate).toLocaleDateString('pt-BR')}</p>
                        <p className="text-sm font-medium text-green-600">R$ {item.amount.toLocaleString('pt-BR')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Calendar Table */}
              {!calendarData?.calendar || calendarData.calendar.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhuma obrigação fiscal encontrada para este período</p>
                </div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Descrição
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vencimento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cliente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {calendarData.calendar
                        .slice((calendarPage - 1) * calendarItemsPerPage, calendarPage * calendarItemsPerPage)
                        .map((obligation) => (
                        <tr key={obligation.id} className={`hover:bg-gray-50 border-l-4 ${getPriorityColor(obligation.priority)}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {obligation.taxType}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {obligation.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(obligation.dueDate).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {obligation.clientName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            R$ {obligation.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(obligation.status)}`}>
                              {getStatusIcon(obligation.status)}
                              {getStatusLabel(obligation.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {obligation.status !== 'paid' && (
                              <button
                                onClick={() => handleMarkAsPaid(obligation)}
                                className="text-green-600 hover:text-green-800 text-sm font-medium"
                              >
                                Marcar como Pago
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {calendarData.calendar.length > calendarItemsPerPage && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      Mostrando {((calendarPage - 1) * calendarItemsPerPage) + 1} a {Math.min(calendarPage * calendarItemsPerPage, calendarData.calendar.length)} de {calendarData.calendar.length} obrigações
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCalendarPage(p => Math.max(1, p - 1))}
                        disabled={calendarPage === 1}
                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-600">
                        Página {calendarPage} de {Math.ceil(calendarData.calendar.length / calendarItemsPerPage)}
                      </span>
                      <button
                        onClick={() => setCalendarPage(p => Math.min(Math.ceil(calendarData.calendar.length / calendarItemsPerPage), p + 1))}
                        disabled={calendarPage >= Math.ceil(calendarData.calendar.length / calendarItemsPerPage)}
                        className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
            </div>
          )}



          {/* TODO: Pós-MVP - Relatórios fiscais precisam ser melhor elaborados e pensados
          {activeTab === 'reports' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Relatório de Compliance</h3>
                <button
                  onClick={loadComplianceReport}
                  disabled={loadingReport}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingReport ? 'animate-spin' : ''}`} />
                  {loadingReport ? 'Atualizando...' : 'Atualizar'}
                </button>
              </div>

              {loadingReport && !complianceReport && (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                  <span className="ml-3 text-gray-600">Carregando relatório...</span>
                </div>
              )}

              {complianceReport && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm text-gray-600">Visão geral do cumprimento das obrigações fiscais por cliente (últimos 30 dias)</p>
                    <span className="text-sm text-gray-500">
                      Gerado em: {new Date(complianceReport.generatedAt).toLocaleString('pt-BR')}
                    </span>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-600">Total de Clientes</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {complianceReport.complianceMetrics.totalClients}
                      </p>
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-600">Conformes</p>
                      <p className="text-2xl font-bold text-green-600">
                        {complianceReport.complianceMetrics.compliantClients}
                      </p>
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-600">Não Conformes</p>
                      <p className="text-2xl font-bold text-red-600">
                        {complianceReport.complianceMetrics.nonCompliantClients}
                      </p>
                    </div>
                    <div className="p-4 bg-white rounded-lg">
                      <p className="text-sm text-gray-600">Score Médio</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {complianceReport.complianceMetrics.averageComplianceScore}%
                      </p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Análise de Risco</h5>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span className="text-sm text-gray-600">Alto: {complianceReport.riskAnalysis.highRisk}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        <span className="text-sm text-gray-600">Médio: {complianceReport.riskAnalysis.mediumRisk}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        <span className="text-sm text-gray-600">Baixo: {complianceReport.riskAnalysis.lowRisk}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Recomendações</h5>
                    <ul className="space-y-2">
                      {complianceReport.recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Itens de Ação</h5>
                    <div className="space-y-2">
                      {complianceReport.actionItems.map((item, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded-lg border-l-4 bg-white ${
                            item.priority === 'high' ? 'border-l-red-500' :
                            item.priority === 'medium' ? 'border-l-yellow-500' : 'border-l-green-500'
                          }`}
                        >
                          <p className="text-sm font-medium text-gray-900">{item.item}</p>
                          <p className="text-xs text-gray-500">
                            Prazo: {new Date(item.deadline).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          */}

          {SHOW_SETTINGS_TAB && activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Client Select */}
              <div className="flex flex-col md:flex-row gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Selecione o Cliente
                  </label>
                  <Select
                    value={settingsSelectedClientId}
                    onChange={(e) => {
                      const clientId = e.target.value
                      setSettingsSelectedClientId(clientId)
                      if (clientId) {
                        loadClientTaxSettings(clientId)
                      } else {
                        setClientTaxSettings(null)
                      }
                    }}
                    options={[
                      { value: '', label: 'Selecione um cliente...' },
                      ...clientsList.map(c => ({ value: c.id, label: c.name }))
                    ]}
                    fullWidth
                  />
                </div>
              </div>

              {/* Error/Success Messages */}
              {settingsError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-red-800">{settingsError}</p>
                  <button onClick={() => setSettingsError(null)} className="ml-auto text-red-600 hover:text-red-800">×</button>
                </div>
              )}
              {settingsSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-green-800">{settingsSuccess}</p>
                  <button onClick={() => setSettingsSuccess(null)} className="ml-auto text-green-600 hover:text-green-800">×</button>
                </div>
              )}

              {/* Loading State */}
              {loadingSettings && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              )}

              {/* Settings Form */}
              {clientTaxSettings && !loadingSettings && (
                <div className="space-y-4">
                  {/* Header with Save Button */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-gray-600" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {clientTaxSettings.clientName || `Cliente ${clientTaxSettings.clientId}`}
                        </h3>
                        {clientTaxSettings.cnpj && (
                          <p className="text-sm text-gray-500">CNPJ: {clientTaxSettings.cnpj}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowHistoryModal(true)}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        <History className="w-4 h-4" />
                        Histórico
                      </button>
                      <button
                        onClick={saveClientTaxSettings}
                        disabled={savingSettings}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                      >
                        {savingSettings ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        Salvar Configurações
                      </button>
                    </div>
                  </div>

                  {/* Regime Selection Section */}
                  <div className="bg-white border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('regime')}
                      className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">Regime Tributário</span>
                      {expandedSections.regime ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    {expandedSections.regime && (
                      <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          {(taxRegimes || []).map((regime) => (
                            <div
                              key={regime.code}
                              onClick={() => updateSettings('taxRegime', regime.code)}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                                clientTaxSettings.taxRegime === regime.code
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <h4 className="font-medium text-gray-900">{regime.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">{regime.description}</p>
                              {regime.revenueLimit && (
                                <p className="text-xs text-gray-500 mt-2">Limite: {regime.revenueLimit}</p>
                              )}
                            </div>
                          ))}
                        </div>


                      </div>
                    )}
                  </div>

                  {/* Simples Nacional Section - Only show if regime is 'simples' */}
                  {clientTaxSettings.taxRegime === 'simples' && (
                    <div className="bg-white border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleSection('simplesNacional')}
                        className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                      >
                        <span className="font-medium text-gray-900">Configurações Simples Nacional</span>
                        {expandedSections.simplesNacional ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                      {expandedSections.simplesNacional && (
                        <div className="px-4 pb-4 border-t border-gray-200 pt-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Anexo</label>
                              <Select
                                value={clientTaxSettings.simplesNacional?.annex || 'III'}
                                onChange={(e) => updateSettings('simplesNacional.annex', e.target.value)}
                                options={[
                                  { value: 'III', label: 'Anexo III - Serviços em geral' },
                                  { value: 'V', label: 'Anexo V - Serviços profissionais' }
                                ]}
                                fullWidth
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">RBT12 (Receita Bruta 12 meses)</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                                <input
                                  type="text"
                                  value={(clientTaxSettings.simplesNacional?.rbt12 || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/\./g, '').replace(',', '.')
                                    const numValue = parseFloat(value) || 0
                                    updateSettings('simplesNacional.rbt12', numValue)
                                  }}
                                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="0,00"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Fator R (Folha/Receita)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                value={clientTaxSettings.simplesNacional?.factorR || 0}
                                onChange={(e) => updateSettings('simplesNacional.factorR', parseFloat(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">Se ≥ 0.28, pode usar Anexo III</p>
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={clientTaxSettings.simplesNacional?.useFactorROptimization || false}
                                  onChange={(e) => updateSettings('simplesNacional.useFactorROptimization', e.target.checked)}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">Otimizar com Fator R</span>
                              </label>
                            </div>
                          </div>

                          {/* Calculate Rate Button */}
                          <div className="flex items-center gap-4">
                            <button
                              onClick={calculateSimplesRate}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                            >
                              <Calculator className="w-4 h-4" />
                              Calcular Alíquota Efetiva
                            </button>
                            {simplesCalculation && (
                              <div className="flex-1 p-3 bg-green-50 rounded-lg">
                                <div className="flex items-center gap-4">
                                  <div>
                                    <span className="text-sm text-gray-600">Alíquota Efetiva:</span>
                                    <span className="ml-2 text-lg font-bold text-green-700">{simplesCalculation.effectiveRate.toFixed(2)}%</span>
                                  </div>
                                  <div>
                                    <span className="text-sm text-gray-600">Faixa:</span>
                                    <span className="ml-2 font-medium">{simplesCalculation.band}ª</span>
                                  </div>
                                  {simplesCalculation.recommendation && (
                                    <div className="flex-1 text-sm text-green-700">
                                      <Info className="w-4 h-4 inline mr-1" />
                                      {simplesCalculation.recommendation}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ISS Settings Section */}
                  <div className="bg-white border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('iss')}
                      className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">Configurações ISS</span>
                      {expandedSections.iss ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    {expandedSections.iss && (
                      <div className="px-4 pb-4 border-t border-gray-200 pt-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">UF</label>
                            <Select
                              onChange={(e) => loadISSRates(e.target.value)}
                              options={[
                                { value: '', label: 'Selecione o estado' },
                                ...['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => ({
                                  value: uf,
                                  label: uf
                                }))
                              ]}
                              fullWidth
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Município</label>
                            <Select
                              value={clientTaxSettings.iss?.municipalityCode || ''}
                              onChange={(e) => {
                                const selected = issRates.find(r => r.municipalityCode === e.target.value)
                                if (selected) {
                                  updateSettings('iss', {
                                    ...clientTaxSettings.iss,
                                    municipalityCode: selected.municipalityCode,
                                    municipalityName: selected.municipalityName,
                                    aliquot: selected.medicalRate
                                  })
                                }
                              }}
                              options={[
                                { value: '', label: 'Selecione o município' },
                                ...issRates.map(rate => ({
                                  value: rate.municipalityCode,
                                  label: `${rate.municipalityName} - ${rate.medicalRate}%`
                                }))
                              ]}
                              fullWidth
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Alíquota ISS (%)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={clientTaxSettings.iss?.aliquot || 0}
                              onChange={(e) => updateSettings('iss.aliquot', parseFloat(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.iss?.hasIncentive || false}
                                onChange={(e) => updateSettings('iss.hasIncentive', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Possui incentivo fiscal</span>
                            </label>
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.iss?.nfseEnabled || false}
                                onChange={(e) => updateSettings('iss.nfseEnabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">NFS-e habilitada</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Withholdings Settings Section */}
                  <div className="bg-white border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('withholdings')}
                      className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">Retenções na Fonte</span>
                      {expandedSections.withholdings ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    {expandedSections.withholdings && (
                      <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {/* IRRF */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.withholdings?.irrf?.enabled || false}
                                onChange={(e) => updateSettings('withholdings.irrf.enabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-700">IRRF</span>
                            </label>
                            {clientTaxSettings.withholdings?.irrf?.enabled && (
                              <div className="space-y-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="Alíquota %"
                                  value={clientTaxSettings.withholdings?.irrf?.rate || ''}
                                  onChange={(e) => updateSettings('withholdings.irrf.rate', parseFloat(e.target.value))}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">R$</span>
                                  <input
                                    type="text"
                                    placeholder="Limite de isenção"
                                    value={clientTaxSettings.withholdings?.irrf?.threshold ? clientTaxSettings.withholdings.irrf.threshold.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : ''}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/\./g, '').replace(',', '.')
                                      updateSettings('withholdings.irrf.threshold', parseFloat(value) || 0)
                                    }}
                                    className="w-full pl-7 pr-2 py-1 text-sm border border-gray-300 rounded"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* PIS */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.withholdings?.pis?.enabled || false}
                                onChange={(e) => updateSettings('withholdings.pis.enabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-700">PIS</span>
                            </label>
                            {clientTaxSettings.withholdings?.pis?.enabled && (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Alíquota %"
                                value={clientTaxSettings.withholdings?.pis?.rate || ''}
                                onChange={(e) => updateSettings('withholdings.pis.rate', parseFloat(e.target.value))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            )}
                          </div>

                          {/* COFINS */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.withholdings?.cofins?.enabled || false}
                                onChange={(e) => updateSettings('withholdings.cofins.enabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-700">COFINS</span>
                            </label>
                            {clientTaxSettings.withholdings?.cofins?.enabled && (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Alíquota %"
                                value={clientTaxSettings.withholdings?.cofins?.rate || ''}
                                onChange={(e) => updateSettings('withholdings.cofins.rate', parseFloat(e.target.value))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            )}
                          </div>

                          {/* CSLL */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.withholdings?.csll?.enabled || false}
                                onChange={(e) => updateSettings('withholdings.csll.enabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-700">CSLL</span>
                            </label>
                            {clientTaxSettings.withholdings?.csll?.enabled && (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Alíquota %"
                                value={clientTaxSettings.withholdings?.csll?.rate || ''}
                                onChange={(e) => updateSettings('withholdings.csll.rate', parseFloat(e.target.value))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            )}
                          </div>

                          {/* INSS */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.withholdings?.inss?.enabled || false}
                                onChange={(e) => updateSettings('withholdings.inss.enabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-700">INSS</span>
                            </label>
                            {clientTaxSettings.withholdings?.inss?.enabled && (
                              <div className="space-y-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="Alíquota %"
                                  value={clientTaxSettings.withholdings?.inss?.rate || ''}
                                  onChange={(e) => updateSettings('withholdings.inss.rate', parseFloat(e.target.value))}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                                <div className="relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">R$</span>
                                  <input
                                    type="text"
                                    placeholder="Teto"
                                    value={clientTaxSettings.withholdings?.inss?.ceiling ? clientTaxSettings.withholdings.inss.ceiling.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : ''}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/\./g, '').replace(',', '.')
                                      updateSettings('withholdings.inss.ceiling', parseFloat(value) || 0)
                                    }}
                                    className="w-full pl-7 pr-2 py-1 text-sm border border-gray-300 rounded"
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* ISS Retido */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.withholdings?.iss?.enabled || false}
                                onChange={(e) => updateSettings('withholdings.iss.enabled', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="font-medium text-gray-700">ISS Retido</span>
                            </label>
                            {clientTaxSettings.withholdings?.iss?.enabled && (
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Alíquota %"
                                value={clientTaxSettings.withholdings?.iss?.rate || ''}
                                onChange={(e) => updateSettings('withholdings.iss.rate', parseFloat(e.target.value))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Accessory Obligations Section */}
                  <div className="bg-white border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('obligations')}
                      className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">Obrigações Acessórias</span>
                      {expandedSections.obligations ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    {expandedSections.obligations && (
                      <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {accessoryObligations
                            .filter(ob => ob.applicableRegimes.includes(clientTaxSettings.taxRegime))
                            .map(obligation => (
                              <label
                                key={obligation.code}
                                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                  clientTaxSettings.accessoryObligations?.includes(obligation.code)
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={clientTaxSettings.accessoryObligations?.includes(obligation.code) || false}
                                  onChange={() => toggleObligation(obligation.code)}
                                  className="w-4 h-4 mt-0.5 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <div>
                                  <span className="font-medium text-gray-900">{obligation.code}</span>
                                  <p className="text-sm text-gray-600">{obligation.name}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {obligation.frequency === 'monthly' && 'Mensal'}
                                    {obligation.frequency === 'quarterly' && 'Trimestral'}
                                    {obligation.frequency === 'annual' && 'Anual'}
                                    {obligation.frequency === 'event' && 'Por evento'}
                                    {' - '}{obligation.deadline}
                                  </p>
                                </div>
                              </label>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notification Preferences Section */}
                  <div className="bg-white border border-gray-200 rounded-lg">
                    <button
                      onClick={() => toggleSection('notifications')}
                      className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">Preferências de Notificação</span>
                      {expandedSections.notifications ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    {expandedSections.notifications && (
                      <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <h5 className="font-medium text-gray-700">Lembretes</h5>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.notificationPreferences?.obligationReminder || false}
                                onChange={(e) => updateSettings('notificationPreferences.obligationReminder', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Lembrete de obrigações</span>
                            </label>
                            {clientTaxSettings.notificationPreferences?.obligationReminder && (
                              <div className="ml-6">
                                <label className="text-xs text-gray-500">Dias antes:</label>
                                <input
                                  type="number"
                                  value={clientTaxSettings.notificationPreferences?.obligationReminderDays || 5}
                                  onChange={(e) => updateSettings('notificationPreferences.obligationReminderDays', parseInt(e.target.value))}
                                  className="ml-2 w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                                />
                              </div>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.notificationPreferences?.paymentReminder || false}
                                onChange={(e) => updateSettings('notificationPreferences.paymentReminder', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Lembrete de pagamento</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.notificationPreferences?.rateChanges || false}
                                onChange={(e) => updateSettings('notificationPreferences.rateChanges', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Alterações de alíquotas</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.notificationPreferences?.legislationUpdates || false}
                                onChange={(e) => updateSettings('notificationPreferences.legislationUpdates', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Atualizações de legislação</span>
                            </label>
                          </div>

                          <div className="space-y-3">
                            <h5 className="font-medium text-gray-700">Canais</h5>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.notificationPreferences?.emailNotifications || false}
                                onChange={(e) => updateSettings('notificationPreferences.emailNotifications', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">E-mail</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.notificationPreferences?.smsNotifications || false}
                                onChange={(e) => updateSettings('notificationPreferences.smsNotifications', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">SMS</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={clientTaxSettings.notificationPreferences?.pushNotifications || false}
                                onChange={(e) => updateSettings('notificationPreferences.pushNotifications', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-700">Push</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Internal Notes */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Observações Internas</label>
                    <textarea
                      value={clientTaxSettings.internalNotes || ''}
                      onChange={(e) => updateSettings('internalNotes', e.target.value)}
                      rows={3}
                      placeholder="Notas internas sobre as configurações fiscais deste cliente..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!clientTaxSettings && !loadingSettings && (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum cliente selecionado</h3>
                  <p className="text-gray-600">
                    Busque um cliente pelo nome, CNPJ ou CRM para visualizar e editar suas configurações fiscais.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* History Modal */}
          {SHOW_SETTINGS_TAB && showHistoryModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Histórico de Alterações</h3>
                  <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                  {settingsHistory.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Nenhuma alteração registrada</p>
                  ) : (
                    <div className="space-y-4">
                      {settingsHistory.map((entry) => (
                        <div key={entry.id} className="border-l-4 border-blue-500 pl-4 py-2">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">{entry.description}</p>
                              <p className="text-sm text-gray-500">
                                Por {entry.changedByName || entry.changedBy} em{' '}
                                {new Date(entry.changedAt).toLocaleString('pt-BR')}
                              </p>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              entry.changeType === 'regime_change' ? 'bg-purple-100 text-purple-800' :
                              entry.changeType === 'created' ? 'bg-green-100 text-green-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {entry.changeType === 'regime_change' && 'Mudança de Regime'}
                              {entry.changeType === 'created' && 'Criação'}
                              {entry.changeType === 'updated' && 'Atualização'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
